import React, { useState, useMemo } from 'react';
import { Table, Button, Popconfirm, Input, Space, Tag } from 'antd';
import { useSupplier } from '../../store/supplierContext';
import { Supplier } from '../../types/supplier';
import { useTranslation } from "react-i18next";
import { EditOutlined, DeleteOutlined } from '@ant-design/icons';
import PageNavigation from '../Layout/PageNavigation';

interface SupplierListProps {
    onEdit: (supplier: Supplier) => void;
}

const SupplierList: React.FC<SupplierListProps> = ({ onEdit }) => {
    const { suppliers, deleteSupplier } = useSupplier();
    const { t, i18n } = useTranslation();
    const [search, setSearch] = useState<string>('');

    const filteredSuppliers = useMemo(() => {
        return suppliers.filter(s =>
            s.name.toLowerCase().includes(search.toLowerCase()) ||
            (s.phone || '').includes(search) ||
            (s.email || '').includes(search)
        );
    }, [suppliers, search]);

    const columns = [
        { title: t('name'), dataIndex: 'name' },
        { title: t('phone'), dataIndex: 'phone' },
        { title: t('email'), dataIndex: 'email' },
        { title: t('address'), dataIndex: 'address' },
        {
            title: t('balances'),
            dataIndex: 'balances',
            render: (balances: any[]) =>
                balances?.length
                    ? (
                        <Space size={0} direction="vertical">
                            {balances.map((b, idx) =>
                                <Tag key={idx} color="blue" style={{ marginBottom: 2 }}>
                                    {b.currency}: <b>{b.balance}</b>
                                </Tag>
                            )}
                        </Space>
                    )
                    : '-'
        },
        {
            title: t('actions'),
            render: (_: any, record: Supplier) => (
                <Space>
                    <Button
                        type="primary"
                        icon={<EditOutlined />}
                        onClick={() => onEdit(record)}
                    >
                        {t('edit')}
                    </Button>
                    <Popconfirm
                        title={t('sure_to_delete') as string}
                        onConfirm={() => deleteSupplier(record.id!)}
                    >
                        <Button type="primary" danger icon={<DeleteOutlined />}>
                            {t('delete')}
                        </Button>
                    </Popconfirm>
                </Space>
            ),
        },
    ];

    return (
        <div style={{
            overflowX: 'auto',
            direction: i18n.language === 'ar' || i18n.language === 'ckb' ? 'rtl' : 'ltr',
        }}>
            <PageNavigation title={t('suppliers') as string} />
            <Input.Search
                allowClear
                style={{ marginBottom: 16, maxWidth: 300 }}
                placeholder={t('search_suppliers') as string}
                onChange={(e) => setSearch(e.target.value)}
                value={search}
            />
            <Table
                rowKey={record => record.id as string}
                dataSource={filteredSuppliers}
                columns={columns}
                scroll={{ x: 'max-content' }}
                pagination={{ pageSize: 6 }}
            />
        </div>
    );
};

export default SupplierList;
